import { Head } from "@inertiajs/react";
import L from "leaflet";
import { useState } from "react";
import "leaflet/dist/leaflet.css";
import { MapContainer, Marker } from "react-leaflet";
import { SalonpageTileLayer } from "./_map-tile-layer";
import {
  encodeSalonpagePathId,
  hairbookPostThumbnailR2Url,
  salonpageR2ImageUrl,
  salonpageR2MenuImageUrl,
  salonpageVideoMp4Url,
  salonpageVideoPosterUrl,
  salonpageVideoThumbnailUrl,
} from "@/lib/_url";
import { useVideoPlayer } from "@/lib/_use-video-player";
import { SalonpageFooter } from "./_footer";
import { GoogleMapsFloatingLink } from "./_google_maps_floating_link";
import { NoroshiReserveCta } from "./_noroshi-reserve-cta";
import { NoroshiReserveHost } from "./_noroshi-reserve-host";

interface Salon {
  id: string;
  name: string;
  nameKana: string;
  address: string;
  latitude: number | string | null;
  longitude: number | string | null;
  access: string;
  businessHours: string;
  holiday: string;
  reserveUrl: string;
}

interface StaffItem {
  id: string;
  name: string;
  kana: string;
  role: string;
  history: string;
  description: string;
  imageUrl: string;
  profileVideoId?: string | null;
  profileVideoOrientation?: string | null;
  reserveUrl: string;
  hasWebReservation?: boolean | number | null;
  position: number;
}

interface StyleItem {
  id: string;
  name: string;
  staffName: string;
  imageUrl: string;
  styleUrl: string;
  position: number;
}

interface MenuItem {
  id: string;
  category: string;
  name: string;
  priceLabel: string;
  description: string;
  reserveUrl: string;
  imageId?: string;
  imageAlt?: string;
  position: number;
}

interface PostItem {
  id: string;
  publishedAt: string;
  videoOrientation: string;
}

interface HeroVideoItem {
  videoId: string;
  videoOrientation?: string | null;
  position?: number | null;
}

interface Meta {
  title: string;
  description: string;
}

interface FeatureToggles {
  noroshiReserve?: boolean | number | null;
}

interface Props {
  salon?: Salon;
  staff?: StaffItem | null;
  styles?: StyleItem[];
  heroVideoId?: string | null;
  heroVideoOrientation?: string | null;
  heroVideos?: HeroVideoItem[];
  menus?: MenuItem[];
  posts?: PostItem[];
  featureToggles?: FeatureToggles;
  meta?: Meta;
}

export default function SalonStaff(props: Props) {
  const reserveHost = props.salon?.id ? (
    <NoroshiReserveHost salonId={props.salon.id} enabled={props.featureToggles?.noroshiReserve} />
  ) : null;

  if (!props.salon || !props.staff || !props.staff.imageUrl) {
    return (
      <>
        {reserveHost}
        <NotFound label="Staff not found." />
      </>
    );
  }

  const salon = props.salon;
  const staff = props.staff;
  const styles = props.styles ?? [];
  const menus = props.menus ?? [];
  const posts = props.posts ?? [];
  const meta = props.meta ?? { title: staff.name, description: `${salon.name}のスタッフ詳細です。` };
  const tags = specialtyTags(staff);
  const displayName = salon.name.split("【")[0].trim();
  const heroVideos = normalizedVideoItems(props.heroVideos, props.heroVideoId, props.heroVideoOrientation);
  const profileVideos = normalizedVideoItems(undefined, staff.profileVideoId, staff.profileVideoOrientation);
  const hasHeroVideo = heroVideos.length > 0;
  const displayVideos = hasHeroVideo ? heroVideos : profileVideos;
  const isHeroMode = hasHeroVideo;
  const heroContainerClass = isHeroMode ? "relative bg-zinc-950" : "absolute inset-0";
  const heroMediaContainerClass = isHeroMode ? "relative overflow-hidden bg-zinc-950" : "h-full w-full";
  const heroMediaClass = isHeroMode ? "w-full h-auto" : "h-full w-full object-cover";
  const heroFallbackClass = isHeroMode ? "w-full h-auto" : "h-full w-full object-cover";

  return (
    <>
      <Head title={meta.title}>
        <meta name="description" content={meta.description} />
      </Head>

      <div className="min-h-screen bg-neutral-200 text-zinc-950 antialiased">
        <main className="mx-auto min-h-screen max-w-[448px] bg-white shadow-[0_0_24px_rgba(15,23,42,0.08)]">
          <section className={`relative overflow-hidden ${hasHeroVideo ? "bg-white text-zinc-950" : "min-h-[82svh] bg-zinc-950 text-white"}`}>
            <header className="absolute left-0 right-0 top-0 z-10 px-4 pt-4">
              <div className="flex items-center justify-between">
                <a href={`/salons/${encodeSalonpagePathId(salon.id)}/staffs`} aria-label="スタッフ一覧へ戻る" className="grid h-9 w-9 place-items-center rounded-full bg-black/35 text-xl leading-none text-white backdrop-blur">
                  ‹
                </a>
                <a href={`/salons/${encodeSalonpagePathId(salon.id)}`} className="rounded-full bg-black/30 px-3 py-2 text-xs font-bold text-white backdrop-blur">
                  サロンTOP
                </a>
              </div>
            </header>

            <div className={heroContainerClass}>
              <StaffProfileMedia
                salon={salon}
                staff={staff}
                videos={displayVideos}
                className={heroMediaContainerClass}
                mediaClassName={heroMediaClass}
                fallbackClassName={heroFallbackClass}
              />
            </div>
            {!hasHeroVideo && <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/82" />}
            <StaffHeroProfile
              salon={salon}
              staff={staff}
              tags={tags}
              overlap={!hasHeroVideo}
              noroshiReserveEnabled={props.featureToggles?.noroshiReserve}
            />
          </section>

          {styles.length > 0 && (
            <FeaturedStylesSection salon={salon} staff={staff} styles={styles} />
          )}

          {menus.length > 0 && (
            <section className="border-t border-gray-100 p-4">
              <SectionHeader title="人気メニュー" href={menusIndexUrl(salon, staff)} />
              <div className="space-y-2.5">
                {menus.slice(0, 4).map((menu) => (
                  <a key={menu.id} href={menuDetailUrl(salon, menu, staff)} className="flex min-h-24 items-center gap-3 rounded-md border border-gray-100 bg-white p-2 shadow-[0_8px_22px_rgba(15,23,42,0.05)]">
                    {menu.imageId && (
                      <img
                        src={salonpageR2MenuImageUrl(salon.id, menu.imageId, "thumbnail")}
                        alt={menu.imageAlt || menu.name}
                        className="h-20 w-20 shrink-0 rounded-md bg-gray-100 object-cover"
                        loading="lazy"
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-[11px] font-bold text-zinc-400">{menu.category || "メニュー"}</p>
                          <h3 className="mt-1 line-clamp-2 text-sm font-bold leading-5">{menu.name}</h3>
                        </div>
                        <span className="shrink-0 text-lg leading-6 text-zinc-300">›</span>
                      </div>
                      <div className="mt-2 flex items-end justify-between gap-3">
                        <p className="line-clamp-1 text-xs leading-5 text-zinc-500">{menu.description}</p>
                        <p className="shrink-0 text-sm font-bold text-zinc-900">{menu.priceLabel}</p>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            </section>
          )}

          {posts && posts.length > 0 && (
            <section className="border-t border-gray-100 p-4">
              <SectionHeader title="投稿" />
              <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {posts.slice(0, 8).map((post) => (
                  <div key={post.id} className="w-20 shrink-0 overflow-hidden rounded-md bg-gray-100">
                    <img
                      src={hairbookPostThumbnailR2Url(post.id)}
                      alt=""
                      className="aspect-[3/4] w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>
            </section>
          )}

          <section id="access" className="border-t border-gray-100">
            <div className="space-y-4 px-4 py-5">
              <h2 className="text-xl font-bold tracking-tight">店舗情報</h2>
              <InfoRow label="住所" value={salon.address} />
              <InfoRow label="アクセス" value={salon.access} />
              <InfoRow label="営業時間" value={salon.businessHours} />
              <InfoRow label="定休日" value={salon.holiday} />
            </div>
            <SalonMap salon={salon} displayName={displayName} />
          </section>
        </main>
        {reserveHost}
        <SalonpageFooter salon={salon} current="staff" />
      </div>
    </>
  );
}

function StaffHeroProfile({
  salon,
  staff,
  tags,
  overlap,
  noroshiReserveEnabled,
}: {
  salon: Salon
  staff: StaffItem
  tags: string[]
  overlap: boolean
  noroshiReserveEnabled?: boolean | number | null
}) {
  return (
    <div className={overlap ? "absolute inset-x-0 bottom-0 z-10 px-4 pb-7" : "relative z-10 space-y-0 bg-white px-4 py-5 text-zinc-950"}>
      <p className={`text-sm font-bold ${overlap ? "text-white/85" : "text-zinc-500"}`}>{salon.name}</p>
      <h1 className={`mt-3 text-[34px] font-bold leading-none ${overlap ? "" : "tracking-tight"}`}>{staff.name}</h1>
      {staff.kana && <p className={`mt-2 text-sm font-medium ${overlap ? "text-white/78" : "text-zinc-500"}`}>{staff.kana}</p>}
      {staff.role && <p className={`mt-4 line-clamp-2 text-sm font-bold leading-6 ${overlap ? "text-white/92" : "text-zinc-800"}`}>{staff.role}</p>}
      {staff.description && <p className={`mt-3 line-clamp-2 text-sm leading-6 ${overlap ? "text-white/88" : "text-zinc-600"}`}>{staff.description}</p>}
      <div className="mt-4 flex flex-wrap gap-2">
        {staff.history && <HeroTag overlap={overlap}>{staff.history}</HeroTag>}
        {tags.map((tag) => <HeroTag key={tag} overlap={overlap}>{tag}</HeroTag>)}
      </div>
      {staffCanReserve(staff) && (
        <NoroshiReserveCta
          salonId={salon.id}
          enabled={noroshiReserveEnabled}
          staffId={staff.id}
          className={`mt-5 flex h-[52px] items-center justify-between rounded-full px-5 text-sm font-bold shadow-[0_12px_30px_rgba(0,0,0,0.16)] ${overlap ? "bg-white text-zinc-950" : "bg-zinc-950 text-white"}`}
        >
          <span>Web予約する</span>
          <span className="text-lg">›</span>
        </NoroshiReserveCta>
      )}
    </div>
  );
}

function StaffProfileMedia({
  salon,
  staff,
  videos,
  className,
  mediaClassName,
  fallbackClassName,
}: {
  salon: Salon;
  staff: StaffItem;
  videos?: HeroVideoItem[];
  className?: string;
  mediaClassName?: string;
  fallbackClassName?: string;
}) {
  const posterUrl = salonpageR2ImageUrl(salon.id, staff.id, "thumbnail");
  const videoItems = videos || [];
  const selectedVideo = videoItems[0];
  const videoUrl = selectedVideo ? salonpageVideoMp4Url(selectedVideo.videoId) : null;
  const selectedPosterUrl = selectedVideo ? salonpageVideoPosterUrl(selectedVideo.videoId) : null;
  const hasVideoChoices = videoItems.length > 1;

  return (
    <StaffProfileVideo
      videos={videoItems}
      videoUrl={videoUrl}
      posterUrl={selectedPosterUrl || posterUrl}
      alt={staff.name}
      videoId={selectedVideo?.videoId}
      className={className}
      hasVideoChoices={hasVideoChoices}
      mediaClassName={mediaClassName}
      fallbackClassName={fallbackClassName}
    />
  );
}

function StaffProfileVideo({
  videoUrl,
  posterUrl,
  alt,
  videos,
  videoId,
  hasVideoChoices,
  className = "aspect-[9/16] overflow-hidden rounded-md bg-gray-100",
  mediaClassName = "h-full w-full object-cover",
  fallbackClassName = "aspect-[9/16] w-full rounded-md bg-gray-100 object-cover",
}: {
  videoUrl: string | null;
  posterUrl: string;
  alt: string;
  videos: HeroVideoItem[];
  videoId?: string;
  hasVideoChoices: boolean;
  className?: string;
  mediaClassName?: string;
  fallbackClassName?: string;
}) {
  const [hasError, setHasError] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const selectedVideo = videos[selectedIndex] || videos[0];
  const resolvedVideoId = selectedVideo?.videoId || videoId;
  const resolvedPosterUrl = resolvedVideoId ? salonpageVideoPosterUrl(resolvedVideoId) : posterUrl;
  const selectedVideoUrl = resolvedVideoId ? salonpageVideoMp4Url(resolvedVideoId) : videoUrl;
  const resolvedVideoKey = resolvedVideoId || "staff-profile-video";
  const { videoRef } = useVideoPlayer({
    src: hasError ? null : selectedVideoUrl ?? null,
    poster: resolvedPosterUrl ?? posterUrl,
    muted: true,
    loop: true,
  });

  if (hasError || !videoUrl) {
    return <img src={posterUrl} alt={alt} className={fallbackClassName} />;
  }

  return (
    <div className={`${className} relative`}>
      <video
        ref={videoRef}
        key={resolvedVideoKey}
        aria-label={alt}
        playsInline
        muted
        loop
        preload="metadata"
        poster={resolvedPosterUrl}
        onError={() => setHasError(true)}
        className={mediaClassName}
      />
      {hasVideoChoices && (
        <div className="absolute right-3 bottom-3 left-3 flex gap-1.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {videos.slice(0, 3).map((video, index) => {
            const isSelected = index === selectedIndex;

            return (
              <button
                key={video.videoId}
                type="button"
                onClick={() => setSelectedIndex(index)}
                aria-label={`ヒーロー動画 ${index + 1}`}
                aria-pressed={isSelected}
                className={`h-8 w-10 overflow-hidden rounded-sm border bg-black/30 shadow-sm transition ${isSelected ? "border-white" : "border-white/45 opacity-80 hover:opacity-100"}`}
              >
                <img
                  src={salonpageVideoThumbnailUrl(video.videoId)}
                  alt=""
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function normalizedVideoItems(heroVideos: HeroVideoItem[] | undefined, heroVideoId?: string | null, heroVideoOrientation?: string | null) {
  const videos = (heroVideos || []).filter((video) => video.videoId);
  if (videos.length > 0) return videos;
  return heroVideoId ? [{ videoId: heroVideoId, videoOrientation: heroVideoOrientation, position: 0 }] : [];
}

function FeaturedStylesSection({ salon, staff, styles }: { salon: Salon; staff: StaffItem; styles: StyleItem[] }) {
  const visibleStyles = styles.slice(0, 9);

  return (
    <section className="p-4 pt-5">
      <SectionHeader title="おすすめスタイル" href={stylesIndexUrl(salon, staff)} />
      <div className="grid grid-cols-3 gap-1.5">
        {visibleStyles.map((style) => (
          <a key={style.id} href={styleDetailUrl(salon, style)} className="group block min-w-0 overflow-hidden rounded-md bg-gray-100">
            <img
              src={salonpageR2ImageUrl(salon.id, style.id, "thumbnail")}
              alt={style.name}
              className="aspect-[3/4] w-full object-cover transition duration-300 group-hover:scale-[1.03]"
              loading="lazy"
            />
          </a>
        ))}
      </div>
    </section>
  );
}

function SectionHeader({ title, href }: { title: string; href?: string }) {
  return (
    <div className="mb-4 flex items-center justify-between gap-4">
      <h2 className="text-lg font-bold tracking-tight">{title}</h2>
      {href && <a href={href} className="shrink-0 text-xs font-bold text-zinc-400">すべて見る 〉</a>}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <dl className="grid gap-1 border-t border-gray-100 pt-4">
      <dt className="text-xs font-bold text-zinc-400">{label}</dt>
      <dd className="text-sm leading-6 text-zinc-700">{value}</dd>
    </dl>
  );
}

function SalonMap({ salon, displayName }: { salon: Salon; displayName: string }) {
  const latitude = numericCoordinate(salon.latitude);
  const longitude = numericCoordinate(salon.longitude);
  const hasLocation = latitude != null && longitude != null;

  if (!hasLocation) {
    return (
      <div className="mx-4 mb-5 rounded-md border border-gray-100 bg-gray-50 px-3 py-4 text-xs leading-5 text-zinc-500">
        地図表示に必要な位置情報がまだありません。
      </div>
    );
  }

  const position: [number, number] = [latitude, longitude];

  return (
    <div className="overflow-hidden border-t border-gray-100 bg-gray-50">
      <div className="relative">
        <MapContainer
          center={position}
          zoom={16}
          scrollWheelZoom={false}
          className="aspect-[4/3] w-full z-0"
          attributionControl={false}
          aria-label={`${displayName}の地図`}
        >
          <SalonpageTileLayer />
          <Marker position={position} icon={salonPinIcon} />
        </MapContainer>
        <GoogleMapsFloatingLink latitude={latitude} longitude={longitude} />
      </div>
    </div>
  );
}

function HeroTag({ children, overlap = true }: { children: string; overlap?: boolean }) {
  return <span className={`inline-flex max-w-full truncate rounded px-3 py-1.5 text-xs font-bold ${overlap ? "border border-white/45 bg-black/25 text-white backdrop-blur" : "border border-zinc-200 bg-zinc-50 text-zinc-700"}`}>{children}</span>;
}

function NotFound({ label }: { label: string }) {
  return (
    <main className="grid min-h-screen place-items-center bg-gray-100 px-6 text-zinc-900">
      <p className="text-sm text-zinc-400">{label}</p>
    </main>
  );
}

function styleDetailUrl(salon: Salon, style: StyleItem) {
  return `/salons/${encodeSalonpagePathId(salon.id)}/styles/${encodeSalonpagePathId(style.id)}`;
}

function stylesIndexUrl(salon: Salon, staff: StaffItem) {
  if (!staffCanReserve(staff)) return `/salons/${encodeSalonpagePathId(salon.id)}/styles`;
  const params = new URLSearchParams({ staff_id: staff.id });
  return `/salons/${encodeSalonpagePathId(salon.id)}/styles?${params.toString()}`;
}

function menuDetailUrl(salon: Salon, menu: MenuItem, staff: StaffItem) {
  if (!staffCanReserve(staff)) return `/salons/${encodeSalonpagePathId(salon.id)}/menus/${encodeSalonpagePathId(menu.id)}`;
  const params = new URLSearchParams({ staff_id: staff.id });
  return `/salons/${encodeSalonpagePathId(salon.id)}/menus/${encodeSalonpagePathId(menu.id)}?${params.toString()}`;
}

function menusIndexUrl(salon: Salon, staff: StaffItem) {
  if (!staffCanReserve(staff)) return `/salons/${encodeSalonpagePathId(salon.id)}/menus`;
  const params = new URLSearchParams({ staff_id: staff.id });
  return `/salons/${encodeSalonpagePathId(salon.id)}/menus?${params.toString()}`;
}

function staffCanReserve(staff: StaffItem) {
  return Boolean(staff.reserveUrl) && flagEnabled(staff.hasWebReservation) && !isAssistant(staff);
}

function isAssistant(staff: StaffItem) {
  return staff.id.includes("-assistant-") || staff.role.includes("アシスタント");
}

function flagEnabled(value: boolean | number | null | undefined) {
  return value === true || value === 1;
}

function specialtyTags(staff: StaffItem) {
  const text = `${staff.role} ${staff.description}`;
  return ["髪質改善", "酸性ストレート", "レイヤーカット"].filter((tag) => text.includes(tag));
}

function numericCoordinate(value: number | string | null | undefined) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string") return null;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

const salonPinIcon = L.divIcon({
  className: "salonpage-pin",
  html: `
    <div style="transform: translate(-50%, -100%);">
      <svg width="24" height="32" viewBox="0 0 24 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M12 1.5C6.753 1.5 2.5 5.753 2.5 11c0 7.75 9.5 19.5 9.5 19.5S21.5 18.75 21.5 11C21.5 5.753 17.247 1.5 12 1.5Z" fill="#111827"/>
        <circle cx="12" cy="11" r="4" fill="#F9FAFB"/>
      </svg>
    </div>
  `,
  iconSize: [24, 32],
  iconAnchor: [12, 32],
});
